import React from 'react';

import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Table from 'react-bootstrap/Table';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Pagination from 'react-bootstrap/Pagination';
import Nav from 'react-bootstrap/Nav';
import Dropdown from 'react-bootstrap/Dropdown';

import Toast from 'react-bootstrap/Toast';


import {
    CMD_VARIABLE,
    CMD_TABLE,
    CMD_SYSTEM,
    CMD_VARIABLE_ACTION,
    CMD_TABLE_ACTION,
    CMD_SYSTEM_ACTION,
    ROWS_PER_PAGE,
} from '@appblocks/frontend/client/Components/DeviceDashboard/constants';

import { DatetimePicker, TimePicker } from '@appblocks/frontend/client/Components/DeviceDashboard/TimeComponents';
import SyncStatus from '@appblocks/frontend/cloudclient/Components/SyncStatus';
import { format32bitFloat } from '@appblocks/frontend/client/Components/DeviceDashboard//utils';


/* FRONTEND_START_MARKER */


const FilterType = {
    Contains: 'Contains',
    Equals: 'Equals',
    IsGreaterThan: 'Is greater than',
    IsLessThan: 'Is less than',
    IsEmpty: 'Is empty',
    IsNonEmpty: 'Is non-empty',
};

let filterDebounce;
const debounceTimeout = 1000;

const ImportProgress = ({ importRows }) => {
    let progress = 0;
    for (let i = 0; i < importRows.length; i++) {
        if (importRows[i].loaded) {
            progress++;
        }
    }
    const totalProgress = Math.round(progress / importRows.length * 100);
    return (
        <div>
            <ProgressBar variant="info" now={totalProgress} label={`${totalProgress}%`} />
        </div>
    );
};

const PaginationTable = ({ activeTable, changePage }) => {
    if (activeTable === undefined) {
        return;
    }
    const active = activeTable.page;
    const items = [];
    const remainder = activeTable.count % ROWS_PER_PAGE;
    const pages = (activeTable.count - remainder)
        / ROWS_PER_PAGE + (remainder > 0 ? 1 : 0);
    let minPage = active - 5;
    if (minPage < 0) {
        minPage = 0;
    }
    let maxPage = active + 5;
    if (maxPage >= pages - 1) {
        maxPage = pages - 1;
    }
    for (let number = minPage; number <= maxPage; number++) {
        items.push(
            <Pagination.Item key={number} active={number === (active)} activeLabel="" onClick={() => changePage(number)}>
                {number + 1}
            </Pagination.Item>,
        );
    }
    let max = activeTable.page * ROWS_PER_PAGE + ROWS_PER_PAGE;
    if (max > activeTable.count) {
        max = activeTable.count;
    }

    return (
        <div>
            <Pagination size="sm">
                <Pagination.First onClick={() => changePage(0)} />
                <Pagination.Prev onClick={() => changePage(active - 1)} />
                {items}
                <Pagination.Next onClick={() => changePage(active + 1)} />
                <Pagination.Last onClick={() => changePage(pages - 1)} />
            </Pagination>

            <span>
                {activeTable.page * ROWS_PER_PAGE + 1}
                &nbsp;-&nbsp;
                {max}
            </span>
            <span>
                &nbsp;of&nbsp;
                {activeTable.count}
            </span>

        </div>

    );
};

const FieldInput = ({
    field, index, activeTable, editRow, fieldValueChanged, isInvalid,
}) => {
    const fieldType = field.fieldType;
    const value = editRow[field.name].value;
    if (field.enums !== undefined && field.enums.length > 0
        && fieldType !== 'E' && fieldType !== 'M' // not time and not datetime
    ) {
        return (
            <Form.Select
                value={value}
                onChange={e => fieldValueChanged(e, field.name)}
                className={`${isInvalid ? ' is-invalid' : ''}`}
            >
                {field.enums.map(enumItem => (
                    <option key={enumItem.value} value={enumItem.value}>{`${enumItem.label} (${enumItem.value})`}</option>
                ))}
            </Form.Select>
        );
    }
    switch (fieldType) {
        case 'S':
            return (
                <Form.Control
                    type="text"
                    value={value}
                    onChange={e => fieldValueChanged(e, field.name)}
                    className={`${isInvalid ? ' is-invalid' : ''}`}
                />
            );
        case 'E': {
            const d = new Date(Number(value) * 1000);
            let dValue = '';
            try {
                dValue = (new Date(d.getTime()
                    - d.getTimezoneOffset()
                    * 60000).toISOString()).slice(0, -1);
            } catch (e) {
                console.log(e);
            }
            return (
                <DatetimePicker
                    edit
                    time={Number(value)}
                    className={`${isInvalid ? ' is-invalid' : ''}`}
                    handleChange={(value) => {
                        try {
                            fieldValueChanged({
                                target: {
                                    value,
                                },
                            }, field.name);
                        } catch (e) {
                            console.log(e);
                        }
                    }}
                />
            );
        }
        case 'M': {
            return (
                <TimePicker
                    edit
                    time={Number(value)}
                    className={`${isInvalid ? ' is-invalid' : ''}`}
                    handleChange={(value) => {
                        fieldValueChanged({
                            target: {
                                value,
                            },
                        }, field.name);
                    }}
                />
            );
        }
        default:

            return (
                <Form.Control
                    type="text"
                    className={`${isInvalid ? ' is-invalid' : ''}`}
                    value={value}
                    onChange={e => fieldValueChanged(e, field.name)}
                />
            );
    }
};

const RenderedTable = ({
    activeTable,
    loading,
    refresh,
    showEdit,
    exportTable,
    setSort,
    getSortIcon,
    deleteRow,
    fileImport,
    importTable,
    selectedFields,
    toggleSelectedField,
    applyFilters,
    filters,
    addFilter,
    onChangeFilters,
    removeFilter,
    selectUnselectAll,
    clearFilters,
    allowEdit,
    clearTable,
    setFile,
    file,
}) => {
    const [toggleFilter, setToggleFilter] = React.useState(false);
    const [showImportModal, setShowImportModal] = React.useState(false);
    const fileChanged = (e) => {
        if (e.target.files.length > 0) {
            setFile(e.target.files[0]);
        } else {
            setFile(undefined);
        }
    };

    if (activeTable === undefined) {
        return;
    }
    return (
        <div className="pt-2">
            <div className="d-flex gap-1">
                <Button onClick={() => refresh()} variant="secondary" disabled={loading}>
                    Refresh
                </Button>
                {
                    allowEdit && (
                        <Button onClick={() => showEdit(undefined)} variant="secondary">
                            Add Entry
                        </Button>
                    )
                }
                <Dropdown>
                    <Dropdown.Toggle variant="secondary">
                        Actions
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setToggleFilter(prev => !prev)}>
                            {toggleFilter ? 'Hide Filters' : 'Show Filters'}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => exportTable(undefined)}>
                            Export
                        </Dropdown.Item>
                        {allowEdit && (
                            <Dropdown.Item onClick={() => setShowImportModal(true)}>
                                Import
                            </Dropdown.Item>
                        )}
                        {(allowEdit || activeTable.tableStructure === 'L') && (
                            <Dropdown.Item onClick={() => clearTable()} className="text-danger">
                                Clear Table
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Import Data</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Select File</Form.Label>
                        <Form.Control
                            type="file"
                            ref={fileImport}
                            onChange={fileChanged}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImportModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            importTable();
                            setShowImportModal(false);
                        }}
                        disabled={file === undefined}
                    >
                        Import
                    </Button>
                </Modal.Footer>
            </Modal>
            {
                toggleFilter
                && (
                    <div
                        className="border py-3 px-3 my-1"
                    >
                        <h5>Select fields</h5>
                        {
                            selectedFields.map((field, i) => {
                                return (
                                    <div className="form-check form-check-inline" key={i}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`inlineCheckbox${i}`}
                                            value={`option${i}`}
                                            checked={field.selected}
                                            onClick={() => toggleSelectedField(i)}
                                        />
                                        <label className="form-check-label" htmlFor={`inlineCheckbox${i}`}>{field.name}</label>
                                    </div>
                                );
                            })
                        }
                        <br />
                        <Button onClick={() => selectUnselectAll()} variant="secondary">Select all fields</Button>
                        <h5>Filters</h5>
                        {
                            filters.length > 0
                            && filters.map((filter, index) => {
                                return (
                                    <div className="input-group mb-3" key={index}>
                                        <select className="form-select" value={filter.column} onChange={e => onChangeFilters(index, e, 'column')}>
                                            {
                                                selectedFields.map((field, i) => {
                                                    return (
                                                        <option value={i} key={i}>
                                                            {field.name}
                                                        </option>
                                                    );
                                                })
                                            }
                                        </select>
                                        &nbsp;
                                        <select className="form-select" value={filter.filterType} onChange={e => onChangeFilters(index, e, 'filterType')}>
                                            {
                                                Object.values(FilterType).map((filter, i) => {
                                                    return (
                                                        <option value={filter} key={i}>
                                                            {filter}
                                                        </option>
                                                    );
                                                })
                                            }
                                        </select>
                                        &nbsp;
                                        <input
                                            type="text"
                                            value={filter.filterValue}
                                            className="form-control"
                                            aria-label="Text input with dropdown button"
                                            onChange={e => onChangeFilters(index, e, 'filterValue')}
                                        />
                                        &nbsp;
                                        <Button variant="danger" onClick={e => removeFilter(index)}>Remove</Button>
                                    </div>
                                );
                            })
                        }
                        <Button onClick={() => addFilter()} variant="secondary">Add filter</Button>
                        <br />
                        <br />
                        <Button onClick={() => clearFilters()} variant="secondary">Clear filters</Button>
                    </div>
                )
            }
            <Table striped bordered hover>
                <thead>
                    <tr>
                        {activeTable.fields.map((field, i) => {
                            let headerLabel = `${field.displayName} `;
                            switch (field.fieldType) {
                                case 'S':
                                    headerLabel += '(String)';
                                    break;
                                case 'B':
                                    headerLabel += '(Byte)';
                                    break;
                                case 'W':
                                case 'U':
                                    headerLabel += '(Number)';
                                    break;
                                case 'T':
                                    headerLabel += '(Date/Time)';
                                    break;
                                case 'E':
                                    headerLabel += '(Date/Time)';
                                    break;
                                case 'M':
                                    headerLabel += '(Time)';
                                    break;
                                default:
                                    break;
                            }
                            // if table type is LOG, only TS field is sortable
                            if (activeTable.tableStructure === 'L' && field.displayName !== 'TS') {
                                return (
                                    <th key={i}>{headerLabel}</th>
                                );
                            }
                            return (
                                <th
                                    key={i}
                                    onClick={setSort.bind(this, i)}
                                >
                                    {headerLabel}
                                    &nbsp;&nbsp;
                                    {getSortIcon(i)}
                                </th>
                            );
                        })}
                        {
                            allowEdit && <th />
                        }
                    </tr>
                </thead>
                <tbody>
                    {activeTable.pageData.map((row, i) => {
                        return (
                            <tr key={i}>
                                {row.fields.map((field, j) => {
                                    let fieldValue = field;
                                    if (j >= activeTable.fields.length) {
                                        return;
                                    }
                                    if (activeTable.fields[j].enums !== undefined && activeTable.fields[j].enums.length > 0) {
                                        const fieldLabel = activeTable.fields[j].enums
                                            .find(enumItem => enumItem.value === fieldValue)?.label;
                                        fieldValue = `${fieldLabel} (${fieldValue})`;
                                    }

                                    switch (activeTable.fields[j].fieldType) {
                                        case 'E':
                                            return (
                                                <td key={j}>
                                                    <DatetimePicker
                                                        edit={false}
                                                        time={Number(fieldValue)}
                                                    />
                                                </td>
                                            );
                                        case 'M':
                                            return (
                                                <td key={j}>
                                                    <TimePicker
                                                        edit={false}
                                                        time={Number(fieldValue)}
                                                    />
                                                </td>
                                            );
                                        default:

                                            break;
                                    }
                                    return (
                                        <td key={j}>{fieldValue}</td>
                                    );
                                })}
                                {
                                    allowEdit
                                    && (
                                        <td>
                                            <ButtonGroup className="table-actions">
                                                <Button variant="primary" size="sm" onClick={e => showEdit(row.index, e)}>Edit</Button>
                                                <Button variant="danger" size="sm" onClick={e => deleteRow(row.index, e)}>Delete</Button>
                                            </ButtonGroup>
                                        </td>
                                    )
                                }
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
};

const formatTableData = (rawValue) => {
    let value = rawValue;
    value = value.replace(/^"(.*)"$/, '$1');
    value = value.replaceAll(/""/g, '"');
    return value;
};


const transformData = (result, activeTable) => {
    const data = [];
    const lines = result.split('\r\n');
    for (let i = 0; i < lines.length; i++) {
        if (i === 0) {
            continue;
        }
        if (lines[i].trim() === '') continue;
        const fields = lines[i].split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);
        const row = {
            index: formatTableData(fields[0]),
            fields: [],
        };
        for (let j = 1; j < fields.length; j++) {
            row.fields.push(formatTableData(fields[j]));
        }
        data.push(row);
    }
    activeTable.data = data;
    activeTable.fullData = data;
    activeTable.page = 0;
    const pageData = [];
    for (let i = 0; i < ROWS_PER_PAGE; i++) {
        if (activeTable.data[i] === undefined) {
            break;
        }
        pageData.push(activeTable.data[i]);
    }
    activeTable.pageData = pageData;
    return activeTable;
};

export default class DatabasePage extends React.Component {
    constructor(props) {
        super(props);

        const label = props.tableType === 'T' ? 'Database' : 'Log';

        const tables = [];
        for (let i = 0; i < props.tables.length; i++) {
            const table = props.tables[i];
            if (table.tableStructure !== props.tableType) {
                continue;
            }
            tables.push({
                ...table,
                rawData: '',
            });
        }

        this.state = {
            tables,
            label,
            activeTableIndex: 0,
        };

        this.changeTable = this.changeTable.bind(this);
    }

    changeTable(tableIndex, loadNew = false) {
        this.setState({
            activeTableIndex: tableIndex,
        });
    }

    render() {
        return (
            <div>
                <div
                    className="d-flex align-items-center"
                >
                    <h2>{this.state.label}</h2>
                    {
                        this.props.device && (
                            <>
                                &nbsp;
                                <SyncStatus
                                    device={this.props.device}
                                    propKeys={['tables']}
                                    cancel={(table) => {
                                        this.props.runCommand(CMD_TABLE,
                                            CMD_TABLE_ACTION.CANCEL_CHANGES,
                                            {
                                                table,
                                            });
                                    }}
                                />
                            </>
                        )
                    }
                    {
                        this.props.editPage && (
                            <Button
                                className="ms-auto"
                                style={{
                                    marginRight: 10,
                                }}
                                onClick={() => this.props.editPage('TABLES')}
                            >
                                Edit
                            </Button>
                        )
                    }
                </div>
                <Row id="main">
                    <div className="col-md-12">
                        {this.state.tables.length === 0 ? (
                            <div>
                                No Tables Exposed
                            </div>
                        ) : (
                            <>
                                <Nav
                                    variant="tabs"
                                    activeKey={this.state.activeTableIndex}
                                    onSelect={e => this.setState({ activeTableIndex: e })}
                                >
                                    {
                                        this.state.tables.map((table, i) => {
                                            return (
                                                <Nav.Item key={i}>
                                                    <Nav.Link eventKey={i}>{table.name}</Nav.Link>
                                                </Nav.Item>
                                            );
                                        })
                                    }
                                </Nav>
                                <div
                                    className="bg-white"
                                    style={{
                                        padding: '15px',
                                        border: '1px solid #dee2e6',
                                        borderTop: '0',
                                    }}
                                >
                                    <Database
                                        table={this.state.tables[this.state.activeTableIndex]}
                                        runCommand={this.props.runCommand}
                                        allowEdit={this.props.tableType === 'T'}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </Row>
            </div>
        );
    }
}

export class Database extends React.Component {
    constructor(props) {
        super(props);
        this.fileImport = React.createRef();

        this.state = {
            activeTable: {
                ...props.table,
                allFields: props.table.fields,
                page: 0,
                count: 0,
                pageData: [],
                sortDirection: 0,
                sortField: 0,
            },
            loading: false,
            showEditModal: false,
            rowId: undefined,
            editRow: {},
            showImport: false,
            importRows: [],
            selectedFields: [],
            filters: [],
            snackbarOn: false,
            rawData: props.table.data,
            file: undefined,
        };

        this.saveRow = this.saveRow.bind(this);
        this.cancelImport = this.cancelImport.bind(this);
        this.hideImport = this.hideImport.bind(this);
        this.hideAdd = this.hideAdd.bind(this);
        this.import = this.import.bind(this);
        this.changePage = this.changePage.bind(this);
        this.fieldValueChanged = this.fieldValueChanged.bind(this);
        this.showEdit = this.showEdit.bind(this);
        this.export = this.export.bind(this);
        this.setSort = this.setSort.bind(this);
        this.getSortIcon = this.getSortIcon.bind(this);
        this.deleteRow = this.deleteRow.bind(this);
        this.importRow = this.importRow.bind(this);
        this.toggleSelectedField = this.toggleSelectedField.bind(this);
        this.applyFilters = this.applyFilters.bind(this);
        this.addFilter = this.addFilter.bind(this);
        this.removeFilter = this.removeFilter.bind(this);
        this.onChangeFilters = this.onChangeFilters.bind(this);
        this.selectUnselectAll = this.selectUnselectAll.bind(this);
        this.clearFilters = this.clearFilters.bind(this);
        this.clearTable = this.clearTable.bind(this);
        this.setSnackbarOn = this.setSnackbarOn.bind(this);
        this.applySort = this.applySort.bind(this);
        this.refresh = this.refresh.bind(this);
        this.fetchData = this.fetchData.bind(this);
        this.setFile = this.setFile.bind(this);
        this.fetchCount = this.fetchCount.bind(this);
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.table.name !== this.props.table.name
            || prevProps.table.data !== this.props.table.data
        ) {
            this.initTable();
        }
    }

    onChangeFilters(index, event, element) {
        clearTimeout(filterDebounce);
        const { filters } = this.state;
        filters[index][element] = event.target.value;
        this.setState({
            filters,
        }, () => {
            filterDebounce = setTimeout(() => {
                this.applyFilters();
            }, debounceTimeout);
        });
    }

    setSnackbarOn(value) {
        const { snackbarOn } = this.state;
        const newSnackbarOn = value;
        this.setState({
            snackbarOn: newSnackbarOn,
        });
    }

    getSortIcon(fieldIndex) {
        if (fieldIndex === this.state.activeTable.sortField) {
            if (this.state.activeTable.sortDirection === 0) {
                return <>&uarr;</>;
            }
            return <>&darr;</>;
        }
        return <></>;
    }

    setFile(file) {
        this.setState({
            file,
        });
    }

    setSort(sortField) {
        const { activeTable } = this.state;
        if (sortField === activeTable.sortField) {
            if (activeTable.sortDirection === 0) {
                activeTable.sortDirection = 1;
            } else {
                activeTable.sortDirection = 0;
            }
        } else {
            activeTable.sortField = sortField;
            activeTable.sortDirection = 0;
        }

        this.setState({
            activeTable: {
                ...activeTable,
            },
        }, async () => {
            if (activeTable.tableStructure === 'L') {
                await this.fetchData();
            }
            this.applySort();
        });
    }

    getPages() {
        const remainder = this.state.activeTable.count % ROWS_PER_PAGE;
        const pages = (this.state.activeTable.count - remainder)
            / ROWS_PER_PAGE + (remainder > 0 ? 1 : 0);
        return pages;
    }

    initTable() {
        this.setState({
            activeTable: {
                ...this.props.table,
                allFields: this.props.table.fields,
                page: 0,
                count: 0,
                pageData: [],
                sortDirection: 0,
                sortField: 0,
            },
        }, this.fetchData);
    }

    async applySort(resetPage = true) {
        return new Promise((resolve) => {
            const { activeTable } = this.state;
            const page = resetPage ? 0 : activeTable.page;
            const data = activeTable.data;
            const sortField = activeTable.sortField;
            const sortFieldType = activeTable.allFields[sortField].fieldType;
            data.sort((a, b) => {
                let aa = a;
                let bb = b;
                if (activeTable.sortDirection === 1) {
                    aa = b;
                    bb = a;
                }
                switch (sortFieldType) {
                    case 'F':
                        if (Number(aa.fields[sortField]) > Number(bb.fields[sortField])) {
                            return 1;
                        }
                        if (Number(aa.fields[sortField]) < Number(bb.fields[sortField])) {
                            return -1;
                        }
                        break;
                    default:
                        if (aa.fields[sortField] > bb.fields[sortField]) {
                            return 1;
                        }
                        if (aa.fields[sortField] < bb.fields[sortField]) {
                            return -1;
                        }
                        break;
                }
                return 0;
            });
            activeTable.data = data;
            activeTable.fullData = data;
            activeTable.page = page;
            const pageData = [];
            for (let i = 0; i < ROWS_PER_PAGE; i++) {
                if (activeTable.data[i] === undefined) {
                    break;
                }
                pageData.push(activeTable.data[i]);
            }
            activeTable.pageData = pageData;
            this.setState({
                activeTable,
            }, () => {
                resolve();
            });
        });
    }

    toggleSelectedField(index) {
        clearTimeout(filterDebounce);
        const { selectedFields } = this.state;
        const prevSelected = selectedFields[index].selected;
        selectedFields[index] = {
            ...selectedFields[index],
            selected: !prevSelected,
        };
        this.setState({
            selectedFields,
        }, () => {
            filterDebounce = setTimeout(() => {
                this.applyFilters();
            }, debounceTimeout);
        });
    }

    selectUnselectAll() {
        clearTimeout(filterDebounce);
        const { selectedFields } = this.state;
        let selected = true;
        if (selectedFields.filter(field => field.selected === true).length
            > selectedFields.length / 2) {
            selected = false;
        }
        for (let i = 0; i < selectedFields.length; i++) {
            selectedFields[i].selected = selected;
        }
        this.setState({
            selectedFields,
        }, () => {
            filterDebounce = setTimeout(() => {
                this.applyFilters();
            }, debounceTimeout);
        });
    }

    addFilter() {
        const { filters } = this.state;
        const newFilter = {
            column: 0,
            filterType: FilterType.Contains,
            filterValue: '',
        };
        filters.push(newFilter);
        this.setState({
            filters,
        });
    }

    clearFilters() {
        const { selectedFields, activeTable } = this.state;
        activeTable.data = activeTable.fullData;
        activeTable.count = activeTable.data.length;
        activeTable.fields = activeTable.allFields;
        const newSelectedFields = selectedFields.map((selectedField) => {
            return { ...selectedField, selected: true };
        });
        this.setState({
            activeTable,
            filters: [],
            selectedFields: newSelectedFields,
        }, () => {
            this.changePage(0);
            this.applyFilters();
        });
    }

    async fetchData() {
        this.setState({
            loading: true,
        }, async () => {
            const table = this.state.activeTable;
            let count = 0;
            let offset = 0;
            const totalRows = await this.fetchCount();
            if (table.tableStructure === 'L') {
                count = ROWS_PER_PAGE;
                offset = table.page * ROWS_PER_PAGE;
                // determine sort direction
                if (table.sortDirection === 1) {
                    offset = totalRows - (table.page + 1) * ROWS_PER_PAGE;
                }
                if (offset < 0) {
                    offset = 0;
                }
            }

            const rawData = await this.props.runCommand(
                CMD_TABLE, CMD_TABLE_ACTION.ROWS,
                {
                    table: table.name,
                    count,
                    offset,
                },
            );
            this.setState({
                activeTable: {
                    ...table,
                    count: totalRows,
                },
                rawData,
                loading: false,
            }, () => {
                this.formatData();
            });
        });
    }

    fetchCount() {
        const activeTable = this.state.activeTable;
        return new Promise((resolve) => {
            this.props.runCommand(
                CMD_TABLE, CMD_TABLE_ACTION.COUNT, { table: this.state.activeTable.name },
            )
                .then((result) => {
                    let count = Number(result);
                    if (Number.isNaN(count)) {
                        count = 0;
                    }
                    resolve(count);
                });
        });
    }

    removeFilter(index) {
        clearTimeout(filterDebounce);
        const { filters } = this.state;
        filters.splice(index, 1);
        this.setState({
            filters,
        }, () => {
            filterDebounce = setTimeout(() => {
                this.applyFilters();
            }, debounceTimeout);
        });
    }


    handleCloseSnackBar() {
        this.setSnackbarOn(false);
    }

    clearTable() {
        if (window.confirm(`Clear table ${this.state.activeTable.name}?`)) {
            this.props.runCommand(
                CMD_TABLE, CMD_TABLE_ACTION.CLEAR, { table: this.state.activeTable.name },
            )
                .then((result) => {
                    if (result.trim() !== '') alert(result);
                    this.setSnackbarOn(true);
                    this.fetchData();
                })
                .catch((e) => {
                });
        }
    }

    async applyFilters(resetPage = true) {
        return new Promise((resolve) => {
            const { selectedFields, activeTable, filters } = this.state;
            const includedColumnIndices = [];
            const newIncludedColumnIndices = {};
            for (let i = 0; i < selectedFields.length; i++) {
                if (selectedFields[i].selected) {
                    includedColumnIndices.push(i);
                }
            }
            const newFields = selectedFields.filter(field => field.selected === true);
            let newData = [];
            // apply column filter
            for (let i = 0; i < activeTable.fullData.length; i++) {
                const row = [];
                for (let j = 0; j < includedColumnIndices.length; j++) {
                    row.push(activeTable.fullData[i].fields[includedColumnIndices[j]]);
                    if (!Object.prototype.hasOwnProperty
                        .call(newIncludedColumnIndices, includedColumnIndices[j])) {
                        newIncludedColumnIndices[includedColumnIndices[j]] = row.length - 1;
                    }
                }
                //  start from column 0
                if (row.some(el => el !== null)) {
                    newData.push({
                        index: activeTable.fullData[i].index,
                        fields: row,
                    });
                }
            }
            // apply filters by value
            activeTable.data = newData;
            newData = [];
            for (let i = 0; i < activeTable.data.length; i++) {
                let filteredIn = true;
                const row = activeTable.data[i].fields;
                for (let j = 0; j < filters.length; j++) {
                    const { column, filterType, filterValue } = filters[j];
                    const adjColumn = newIncludedColumnIndices[column];
                    switch (filterType) {
                        case FilterType.Contains:
                            filteredIn = row[adjColumn] !== null && typeof row[adjColumn] !== 'undefined' && row[adjColumn].includes(filterValue);
                            break;
                        case FilterType.Equals:
                            filteredIn = row[adjColumn] === filterValue.toString();
                            break;
                        case FilterType.IsGreaterThan:
                            filteredIn = row[adjColumn] > Number(filterValue);
                            break;
                        case FilterType.IsLessThan:
                            filteredIn = row[adjColumn] < Number(filterValue);
                            break;
                        case FilterType.IsEmpty:
                            filteredIn = row[adjColumn] === null || row[adjColumn] === '' || typeof row[adjColumn] === 'undefined';
                            break;
                        case FilterType.IsNonEmpty:
                            filteredIn = row[adjColumn] !== null && row[adjColumn] !== '' && typeof row[adjColumn] !== 'undefined';
                            break;
                        default:
                            break;
                    }
                    if (!filteredIn) break;
                }
                if (filteredIn) {
                    newData.push({
                        index: activeTable.data[i].index,
                        fields: row,
                    });
                }
            }
            activeTable.data = newData;
            // activeTable.count = newData.length;
            activeTable.fields = newFields;
            const pageData = [];
            for (let i = 0; i < ROWS_PER_PAGE; i++) {
                if (activeTable.data[i] === undefined) {
                    break;
                }
                pageData.push(activeTable.data[i]);
            }
            activeTable.pageData = pageData;
            this.setState({
                activeTable,
            }, () => {
                const pages = this.getPages();
                const page = (resetPage || activeTable.page >= pages) ? 0 : activeTable.page;
                this.changePage(page);
                resolve();
            });
        });
    }

    changePage(page) {
        if (page < 0) {
            return;
        }
        const pages = this.getPages();
        const { activeTable } = this.state;
        if (page === activeTable.page) {
            return;
        }
        if (page >= pages) {
            if (activeTable.data.length === 0) {
                activeTable.pageData = [];
                activeTable.page = 0;
                this.setState({
                    activeTable,
                });
            }
            return;
        }
        activeTable.page = page;
        if (activeTable.tableStructure === 'L') {
            this.setState({
                activeTable: {
                    ...activeTable,
                    page,
                },
            }, () => {
                this.fetchData();
            });
            return;
        }
        const pageData = [];
        for (let i = ROWS_PER_PAGE * page; i < ROWS_PER_PAGE * (page + 1); i++) {
            if (activeTable.data[i] === undefined) {
                break;
            }
            pageData.push(activeTable.data[i]);
        }
        activeTable.pageData = pageData;
        this.setState({
            activeTable,
        });
    }

    parseLogField(field) {
        // row is a string of the form "TS"="1234567890";"MSG"="Hello";"LEVEL"="INFO";
        // parse into JSON object of key-value pairs
        const fieldMap = {};

        // Split by semicolon to get individual key-value pairs
        const pairs = field.split(';').filter(pair => pair.trim() !== '');

        pairs.forEach((pair) => {
            // Split each pair by '=' to separate key and value
            const equalIndex = pair.indexOf('=');
            if (equalIndex !== -1) {
                let key = pair.substring(0, equalIndex).trim();
                let value = pair.substring(equalIndex + 1).trim();

                // Remove surrounding quotes from key and value
                key = key.replace(/^"|"$/g, '');
                value = value.replace(/^"|"$/g, '');

                fieldMap[key] = value;
            }
        });

        return fieldMap;
    }

    async formatData() {
        let { activeTable } = this.state;
        const { rawData } = this.state;
        const page = activeTable.page;
        activeTable = transformData(rawData, activeTable);
        if (activeTable.name === 'LOG') {
            const fields = new Array({
                displayName: 'TS',
                name: 'TS',
                fieldType: 'E',
            });
            const fieldsHashmap = {
                TS: 0,
            };
            // first get total actual table schema
            for (let i = 0; i < activeTable.data.length; i++) {
                activeTable.data[i].fields[1] = activeTable.data[i].fields[1].split(/[=;]+/);
                for (let j = 0; j < activeTable.data[i].fields[1].length - 1; j += 2) {
                    const columnName = activeTable.data[i].fields[1][j];
                    if (!Object.prototype.hasOwnProperty.call(fieldsHashmap, columnName)) {
                        fieldsHashmap[columnName] = fields.length;
                        fields.push({
                            displayName: columnName,
                            name: columnName,
                            fieldType: 'none',
                        });
                    }
                }
            }
            activeTable.fields = fields;
            activeTable.allFields = fields;
            const newData = [];
            // then populate table with values
            for (let i = 0; i < activeTable.data.length; i++) {
                newData.push({
                    index: activeTable.data.index,
                    fields: new Array(fields.length).fill(null),
                });
                newData[i].fields[0] = activeTable.data[i].fields[0];
                for (let j = 1; j < activeTable.data[i].fields[1].length - 1; j += 2) {
                    const key = activeTable.data[i].fields[1][j - 1];
                    const value = activeTable.data[i].fields[1][j];
                    newData[i].fields[fieldsHashmap[key]] = value;
                }
            }
            activeTable.data = newData;
            activeTable.fullData = newData;
            const newPageData = [];
            for (let i = 0; i < ROWS_PER_PAGE; i++) {
                const formattedRow = newData[i];
                if (formattedRow === undefined) {
                    break;
                }
                newPageData.push(formattedRow);
            }
            activeTable.pageData = newPageData;
        }
        activeTable.fields = activeTable.allFields;
        activeTable.page = page;
        const selectedFields = activeTable.fields.map((field) => {
            return {
                ...field,
                selected: true,
            };
        });
        this.setState({
            activeTable,
            selectedFields,
        }, async () => {
            await this.applySort(false);
            await this.applyFilters(false);
        });
    }

    hideAdd() {
        this.setState({
            showEditModal: false,
        });
    }

    showEdit(rowId, event) {
        const editRow = {};
        if (rowId === undefined) {
            for (let i = 0; i < this.state.activeTable.fields.length; i++) {
                const { fieldDefault, name } = this.state.activeTable.fields[i];
                const validateValue = this.validateValue(fieldDefault, name);
                editRow[name] = {
                    value: fieldDefault,
                    ...validateValue,
                };
            }
        } else {
            for (let j = 0; j < this.state.activeTable.pageData.length; j++) {
                if (this.state.activeTable.pageData[j].index === rowId) {
                    for (let i = 0; i < this.state.activeTable.fields.length; i++) {
                        const field = this.state.activeTable.fields[i];
                        const value = this.state.activeTable.pageData[j].fields[i];
                        const validateValue = this.validateValue(value, field.name);
                        editRow[field.name] = {
                            value,
                            ...validateValue,
                        };
                    }
                    break;
                }
            }
        }
        this.setState({
            rowId,
            showEditModal: true,
            editRow,
        });
    }

    hideImport() {
        this.setState({
            showImport: false,
        });
    }

    refresh() {
        this.setState({
            rawData: '',
        }, async () => {
            this.fetchData();
        });
    }

    cancelImport() {
        this.setState({
            showImport: false,
            importRows: [],
        });
    }

    fieldValueChanged(event, name) {
        const { editRow } = this.state;
        const validateValue = this.validateValue(event.target.value, name);
        const value = event.target.value;
        editRow[name] = {
            value,
            ...validateValue,
        };
        this.setState({
            editRow,
        });
    }

    validateValue(value, name) {
        const { activeTable } = this.state;
        const field = activeTable.fields.find(field => field.name === name);
        switch (field.fieldType) {
            case 'F':
                if (Number.isNaN(Number(value)) || value === '') {
                    return {
                        valid: false,
                        message: 'Value must be a number',
                    };
                }
                if ((field.p1 !== '' && Number(value) < Number(field.p1))
                    || (field.p2 !== '' && Number(value) > Number(field.p2))) {
                    let message = `Value must be between ${field.p1} and ${field.p2}`;
                    if (field.p1 === '' && field.p2) {
                        message = `Value must be no more than ${field.p2}`;
                    }
                    if (field.p1 && field.p2 === '') {
                        message = `Value must be no less than ${field.p1}`;
                    }
                    return {
                        valid: false,
                        message,
                    };
                }
                break;
            case 'S':
                if ((field.p1 !== '' && value.length < Number(field.p1))
                    || (field.p2 !== '' && value.length > Number(field.p2))) {
                    let message = `Value must be between ${field.p1} and ${field.p2} characters`;
                    if (field.p1 === '' && field.p2) {
                        message = `Value must be no more than ${field.p2} characters`;
                    }
                    if (field.p1 && field.p2 === '') {
                        message = `Value must be no less than ${field.p1} characters`;
                    }
                    return {
                        valid: false,
                        message,
                    };
                }
                if (value.indexOf('"') !== -1) {
                    return {
                        valid: false,
                        message: 'Value cannot contain double quotes',
                    };
                }
                break;
            default:
                break;
        }
        return {
            valid: true,
            message: '',
        };
    }


    saveRow(e) {
        e.preventDefault();
        let rowStr = '';
        const editRow = this.state.editRow;
        for (let i = 0; i < this.state.activeTable.allFields.length; i++) {
            const { fieldType, name } = this.state.activeTable.allFields[i];
            let fieldValue = editRow[name].value;
            if (fieldType === 'F' && fieldValue !== '0') {
                fieldValue = fieldValue.replace(/^0+/, '');
                fieldValue = format32bitFloat(fieldValue);
            }
            if (Number.isNaN(Number(fieldValue))) {
                rowStr += `"${fieldValue
                    .replaceAll('"', '\\"')}"`;
            } else {
                rowStr += `"${fieldValue}"`;
            }
            if (i < this.state.activeTable.allFields.length - 1) {
                rowStr += ',';
            }
        }
        let action = CMD_TABLE_ACTION.ADD;
        let data = {
            row: rowStr,
            table: this.state.activeTable.name,
        };
        if (this.state.rowId !== undefined) {
            action = CMD_TABLE_ACTION.EDIT;
            data = {
                row: rowStr,
                table: this.state.activeTable.name,
                index: this.state.rowId,
            };
        }
        this.props.runCommand(CMD_TABLE, action, data)
            .then((result) => {
                if (result.trim() !== '') {
                    alert(result);
                } else {
                    this.setState({
                        showEditModal: false,
                    });
                    this.fetchData();
                }
            })
            .catch((e) => {
                console.log(e.message);
            });
    }

    deleteRow(tableRow) {
        const row = this.state.activeTable.page * ROWS_PER_PAGE + Number(tableRow);
        if (window.confirm('Delete this row?')) {
            if (Number.isNaN(row)) {
                return;
            }
            this.props.runCommand(CMD_TABLE, CMD_TABLE_ACTION.DELETE, {
                row,
                table: this.state.activeTable.name,
            })
                .then((result) => {
                    if (result.trim() !== '') {
                        alert(result);
                    } else {
                        this.fetchData();
                    }
                })
                .catch((e) => {

                });
        }
    }

    async export() {
        if (!window.confirm('Export current table?')) {
            return;
        }

        const { allFields } = this.state.activeTable;
        const table = this.state.activeTable;
        const csv = [];

        const totalRows = await this.fetchCount();
        const data = [];
        const lines = [];
        const FETCH_COUNT = 50;
        for (let i = 0; i < totalRows; i += FETCH_COUNT) {
            // eslint-disable-next-line no-await-in-loop
            const result = await this.props.runCommand(
                CMD_TABLE, CMD_TABLE_ACTION.ROWS,
                {
                    table: table.name,
                    count: FETCH_COUNT,
                    offset: i,
                },
            );
            const sectionLines = result.split('\r\n');
            for (let j = 0; j < sectionLines.length; j++) {
                if (j === 0) {
                    continue;
                }
                if (sectionLines[j].trim() === '') continue;
                lines.push(sectionLines[j]);
            }
        }
        for (let j = 0; j < lines.length; j++) {
            if (lines[j].trim() === '') continue;
            const fields = lines[j].split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);
            // remove first field
            const indexValue = fields.shift();
            const row = {
                index: formatTableData(indexValue),
            };
            if (table.name === 'LOG') {
                // push TS field
                row.TS = formatTableData(fields[0]);
                const logFields = this.parseLogField(fields[1]);
                // third field is the log message
                Object.keys(logFields).forEach((key) => {
                    if (allFields.find(field => field.name === key) === undefined) {
                        allFields.push({
                            name: key,
                            displayName: key,
                            fieldType: 'none',
                        });
                    }
                });
                Object.keys(logFields).forEach((key) => {
                    row[key] = logFields[key];
                });
            } else {
                for (let k = 0; k < fields.length; k++) {
                    row[allFields[k].name] = formatTableData(fields[k]);
                }
            }
            data.push(row);
        }
        // eslint-disable-next-line no-useless-escape
        csv.push(allFields.map(field => `\"${field.name}\"`).join(',').concat('\r\n'));
        for (let i = 0; i < data.length; i++) {
            const rowFields = data[i];
            const row = [];
            for (let j = 0; j < allFields.length; j++) {
                const field = allFields[j];
                let fieldValue = '""';
                if (rowFields[field.name] !== undefined) {
                    fieldValue = rowFields[field.name];
                }
                if (field.fieldType === 'E' && this.state.activeTable.name === 'LOG') {
                    // timestamp
                    fieldValue = new Date(Number(fieldValue) * 1000).toISOString();
                }
                row.push(fieldValue);
            }
            csv.push(row.map(data => `\"${data}\"`).join(',').concat('\r\n'));
        }

        const filename = `${this.state.activeTable.name}.csv`;

        const file = new Blob([csv.join('')], { type: 'text/csv' });
        if (window.navigator.msSaveOrOpenBlob) {
            // IE10+
            window.navigator.msSaveOrOpenBlob(file, filename);
        } else { // Others
            const a = document.createElement('a');
            const url = URL.createObjectURL(file);
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 0);
        }
    }

    import(event) {
        const file = this.state.file;
        if (!file) {
            alert('no file selected');
            return;
        }
        const reader = new FileReader();
        this.setState({
            showImport: true,
        });
        reader.onload = async (e) => {
            const contents = e.target.result;
            const result = await this.props
                .runCommand(CMD_TABLE, CMD_TABLE_ACTION.IMPORT, {
                    table: this.state.activeTable.name,
                    contents,
                });
            if (result.toString() === '') {
                // cloud import called
                this.setState({
                    showImport: false,
                });
                return;
            }
            await this.props
                .runCommand(CMD_TABLE, CMD_TABLE_ACTION.CLEAR, {
                    table: this.state.activeTable.name,
                });
            const importRows = contents.split('\r\n').map((row, i) => {
                if (row === '') return null;
                if (i === 0) return null;
                const fields = row.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);
                return {
                    fields,
                    loaded: false,
                };
            }).filter(row => row !== null);
            this.setState({
                importRows,
            }, this.importRow);
        };

        reader.readAsText(file);
    }

    importRow() {
        if (this.state.importRows.length > 0) {
            let editRow;
            let rowStr = '';
            for (let i = 0; i < this.state.importRows.length; i++) {
                if (!this.state.importRows[i].loaded) {
                    editRow = this.state.importRows[i];
                    break;
                }
            }
            if (editRow === undefined) {
                this.setState({
                    showImport: false,
                });
                this.fetchData();
                return;
            }
            for (let i = 0; i < editRow.fields.length; i++) {
                let value = editRow.fields[i]
                    .replace(/^"(.*)"$/, '$1')
                    .replaceAll(/""/g, '\\"');
                if (!Number.isNaN(Number(value))) {
                    value = format32bitFloat(value);
                }
                rowStr += `"${value}"`;
                if (i < editRow.fields.length - 1) {
                    rowStr += ',';
                }
            }
            this.props.runCommand(CMD_TABLE, CMD_TABLE_ACTION.ADD, {
                row: rowStr,
                table: this.state.activeTable.name,
            })
                .then((result) => {
                    const importRows = this.state.importRows;
                    for (let i = 0; i < importRows.length; i++) {
                        if (!importRows[i].loaded) {
                            importRows[i].loaded = true;
                            this.setState({
                                importRows,
                            }, this.importRow);
                            break;
                        }
                    }
                })
                .catch((e) => {

                });
        }
    }

    render() {
        return (
            <div className="database-container">
                <RenderedTable
                    loading={this.state.loading}
                    activeTable={this.state.activeTable}
                    refresh={this.refresh}
                    showEdit={this.showEdit}
                    exportTable={this.export}
                    setSort={this.setSort}
                    getSortIcon={this.getSortIcon}
                    deleteRow={this.deleteRow}
                    fileImport={this.fileImport}
                    importTable={this.import}
                    selectedFields={this.state.selectedFields}
                    toggleSelectedField={this.toggleSelectedField}
                    applyFilters={this.applyFilters}
                    filters={this.state.filters}
                    addFilter={this.addFilter}
                    removeFilter={this.removeFilter}
                    onChangeFilters={this.onChangeFilters}
                    selectUnselectAll={this.selectUnselectAll}
                    clearFilters={this.clearFilters}
                    allowEdit={this.props.allowEdit}
                    clearTable={this.clearTable}
                    setFile={this.setFile}
                    file={this.state.file}
                />
                <div className="pagination">
                    <PaginationTable
                        activeTable={this.state.activeTable}
                        changePage={this.changePage}
                    />
                </div>

                <Modal
                    scrollable
                    show={this.state.showEditModal}
                    onHide={this.hideAdd}
                    dialogClassName="modal-1000w"
                    backdrop="static"
                >
                    <Form onSubmit={this.saveRow}>
                        <Modal.Header closeButton>
                            <Modal.Title>{this.state.rowId ? 'Edit Entry' : 'Add Entry'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>

                            {this.state.activeTable !== undefined
                                ? this.state.activeTable.allFields.map((field, i) => {
                                    const isInvalid = this.state.editRow[field.name]
                                        && this.state.editRow[field.name].valid === false;
                                    return (
                                        <Form.Group key={i}>
                                            <label className="form-label">{field.displayName}</label>
                                            <FieldInput
                                                field={field}
                                                index={i}
                                                activeTable={this.state.activeTable}
                                                editRow={this.state.editRow}
                                                fieldValueChanged={this.fieldValueChanged}
                                                isInvalid={isInvalid}
                                            />
                                            {
                                                isInvalid && (
                                                    <div className="invalid-feedback">
                                                        {this.state.editRow[field.name].message}
                                                    </div>
                                                )
                                            }
                                        </Form.Group>
                                    );
                                })
                                : null}

                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={
                                    Object
                                        .values(this.state.editRow)
                                        .some(field => field.valid === false)
                                }
                            >
                                Ok
                            </Button>
                            <Button variant="secondary" onClick={this.hideAdd}>
                                Cancel
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
                <Modal
                    scrollable
                    show={this.state.showImport}
                    onHide={this.hideImport}
                    dialogClassName="modal-1000w"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Import</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ImportProgress importRows={this.state.importRows} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.cancelImport}>
                            Cancel
                        </Button>
                    </Modal.Footer>
                </Modal>
                <Toast
                    show={this.state.snackbarOn}
                    onClose={() => this.setSnackbarOn(false)}
                    style={{
                        position: 'absolute',
                        bottom: '3rem',
                        right: '3rem',
                    }}
                    delay={3000}
                    autohide
                    bg="dark"
                >
                    <Toast.Body className="text-white">Table cleared</Toast.Body>
                </Toast>
            </div>
        );
    }
}
